import { IManifest } from './models/IManifest';
import { IAppsRequestOptions } from '../models/appsInitialize/IAppsRequestOptions';
import { IAppsRequest, IAppsResponse, IApp } from '../models/appsInitialize';
import { AppParamsHelper } from '../helpers/AppParamsHelper';
import { pivotCollection } from '../utils/collection';
import { deepMergeObjects } from '../utils/object';
import { loadScript } from '../utils/script';
import { IApplication } from '../models/api/applicationManager/IApplication';
import { getElementsForAppInstance, getAppKeyRaw, appendCacheQueryString } from './utils';
import { AppHydration } from './AppHydration';
import { TExternalScriptOptions, TExternalScript } from '../models/utils';
import { CacheOptions } from '../models/appsInitialize/CacheOptions';
import { IAppsRender } from '../models/appsInitialize/IAppsRender';
import { IAppRegistry } from '../models/appsInitialize/IAppRegistry';
import { IAppUrl } from '../models/appsInitialize/IAppUrl';
import { IAppsRenderGroup } from '../models/appsInitialize/IAppsRenderGroup';
import { IAppsRenderVariables } from '../models/appsInitialize/IAppsRenderVariables';
import { IAppsManagerInitializeOptions } from './models/IAppsManagerInitializeOptions';
import { IAppVersions } from './models/IAppVersions';

export const EVENTS = {
	KSYS_APP_LOADED: 'ksys-app-loaded',
	KSYS_APPS_LOADED: 'ksys-apps-loaded',
	KSYS_APP_EMBED_ADDED: 'ksys-app-embed-added',
};

export class AppsManager {
	constructor(public manifest: IManifest, public appHydration: AppHydration, public externalScriptsBasePath?: string) { }
	async initialize(
		options: IAppsManagerInitializeOptions,
	) {
		const {
			initializeAppRequests,
			getApplications,
			externalScriptOptions,
			assetRegisterCacheOptions,
			appsRenderOptions,
			group,
		} = options;

		const environment = process.env.NODE_ENV;
		const templateIds = group && group.apps ? group.apps.map(app => app.templateId) : undefined;
		const elements = this.getAppEmbedNodes(templateIds, (element: HTMLElement) => !element.dataset.managerInitialized);
		if (elements && elements.length > 0) {
			const appsRequest = this.getAppsRequest(options, false, elements);
			if (appsRequest && appsRequest.apps && appsRequest.apps.length > 0) {
				const appsRequests = this.splitAppsRequest(appsRequest);
				const appsResponses = (initializeAppRequests && await initializeAppRequests(appsRequests)) || [];
				const appsResponse = this.appHydration.mergeAppsResponses(appsResponses.filter(appResponse => !(appResponse instanceof Error)));
				this.setInputsOnEmbedContainers(options, elements);
				if (appsResponse) {
					this.mergeMasterStylesAndConfigurations(appsResponse);
					this.appHydration.setHydrationPoint(appsResponse);
					this.loadAppsResponseScripts(appsResponse, externalScriptOptions, assetRegisterCacheOptions, appsRenderOptions);
				}
				else {
					await this.loadAppScriptsForAppsRequest(appsRequest, getApplications, externalScriptOptions);
				}
				elements.forEach((element) => {
					if (element.dataset.ksysAppTemplateId !== 'ksys-app-manager') {
						element.dataset.managerInitialized = 'true';
					}
				});
			}
		}
		return { elements };
	}
	isManagerAppNode(
		appParamsHelper: AppParamsHelper<any, any, any>,
	) {
		const templateId = this.manifest.ksysAppTemplateId;
		const templateIdWithVersion = `${this.manifest.ksysAppTemplateId}-v${
			this.manifest.version
			}`;
		const { ksysAppTemplateId } = appParamsHelper.rawAppParams;
		return [templateId, templateIdWithVersion].includes(ksysAppTemplateId);
	}

	orchestrateAppFromAppParams(
		appParams: AppParamsHelper<any, any, any>,
		includeInputs: boolean = false,
		appVersions?: IAppVersions,
	): IApp {
		const { rawAppParams } = appParams;
		const {
			ksysAppTemplateId,
			applicationCode,
			configurationKey,
			styleKey,
			ksysAppTemplateVersion,
		} = rawAppParams;
		const app: IApp = {
			templateId: ksysAppTemplateId,
		};
		if (applicationCode) {
			app.applicationCode = applicationCode;
		}
		if (configurationKey) {
			app.configurationKey = configurationKey;
		}
		if (styleKey) {
			app.styleKey = styleKey;
		}
		if (ksysAppTemplateVersion) {
			app.version = ksysAppTemplateVersion;
		}
		else if (appVersions && appVersions[ksysAppTemplateId]) {
			app.version = appVersions[ksysAppTemplateId];
			if (!app.version.startsWith('v')) {
				app.version = `v${app.version}`;
			}
		}

		if (includeInputs) {
			app.inputs = appParams.values && appParams.values.inputs;
		}
		return app;
	}
	getAppEmbedNodes(templateIds?: string[], additionalFilterFn?: (element: HTMLElement) => boolean): HTMLElement[] {
		const ksysAppTemplateIdDataKey = 'data-ksys-app-template-id';
		const elements = document.querySelectorAll(`[${ksysAppTemplateIdDataKey}]`);
		let response = Array.from(elements) as HTMLElement[];
		if (templateIds && templateIds.length > 0) {
			response = response.filter((element) => {
				const { ksysAppTemplateId } = element.dataset;
				return ksysAppTemplateId === 'ksys-app-manager' || templateIds.includes(ksysAppTemplateId);
			});
		}
		return response.filter(element => !element.dataset.initialized && (!additionalFilterFn || additionalFilterFn(element)));
	}
	getAppsRequest(options: IAppsManagerInitializeOptions, includeInputs: boolean = false, elements?: HTMLElement[]): IAppsRequest {
		const { url, appsRequestOptions, group, appVersions } = options;
		const appsRequest: IAppsRequest = {
			options: appsRequestOptions,
			apps: [],
		};
		if (!elements) {
			const templateIds = group && group.apps ? group.apps.map(app => app.templateId) : undefined;
			elements = this.getAppEmbedNodes(templateIds, (element: HTMLElement) => !element.dataset.managerInitialized);
		}

		if (elements && elements.length > 0) {
			const appParamHelpers = elements.map(
				element => new AppParamsHelper(element, url),
			);

			const managerAppParams = appParamHelpers.find(appParamsHelper =>
				this.isManagerAppNode(appParamsHelper),
			);

			if (managerAppParams) {
				appsRequest.manager = this.orchestrateAppFromAppParams(
					managerAppParams,
					includeInputs,
				);
				if (
					managerAppParams.values &&
					managerAppParams.values.inputs &&
					(managerAppParams.values.inputs as any).culture
				) {
					if (!appsRequest.options) {
						appsRequest.options = {};
					}
					appsRequest.options.culture = (managerAppParams.values
						.inputs as any).culture;
				}
			}
			const otherAppParams = appParamHelpers.filter(
				appParamsHelper => !this.isManagerAppNode(appParamsHelper),
			);

			for (const appParams of otherAppParams) {
				const app: IApp = this.orchestrateAppFromAppParams(appParams, includeInputs, appVersions);
				if (!appsRequest.apps) {
					appsRequest.apps = [];
				}
				if (appsRequest.apps) {
					appsRequest.apps.push(app);
				}
			}
		}
		return appsRequest;
	}


	setInputsOnEmbedContainers(
		options: IAppsManagerInitializeOptions,
		elements?: HTMLElement[],
	) {
		const appsRequest = this.getAppsRequest(options, true, elements);
		const { manager, apps = [] } = appsRequest;
		if (manager) {
			for (const app of apps) {
				if (app) {
					app.inputs = Object.assign({}, manager.inputs || {}, app.inputs || {});
				}
			}
			for (const app of appsRequest.apps || []) {
				if (app && app.inputs) {
					const inputs = Object.keys(app.inputs).reduce(
						(acc, inputKey) => {
							const key = `input${inputKey
								.substr(0, 1)
								.toUpperCase()}${inputKey.substr(1)}`;
							const value = app.inputs[inputKey];
							acc[key] = value;
							return acc;
						},
						{} as any,
					);
					const elements = getElementsForAppInstance(app);
					if (elements && elements.length > 0) {
						for (const element of elements) {
							if (!element.dataset.ksysAppTemplateVersion && app.version) {
								element.dataset.ksysAppTemplateVersion = app.version;
							}
							for (const key of Object.keys(inputs)) {
								element.dataset[key] = inputs[key];
							}
						}
					}
				}
			}
		}
	}

	mergeMasterStylesAndConfigurations(
		appsResponse: IAppsResponse,
	) {
		const { apps = [], manager } = appsResponse;
		if (manager) {
			for (const appResponse of apps) {
				if (appResponse) {
					appResponse.applicationConfiguration = deepMergeObjects(
						manager.applicationConfiguration || {},
						appResponse.applicationConfiguration || {},
					);
					appResponse.applicationStyles = deepMergeObjects(
						manager.applicationStyles || {},
						appResponse.applicationStyles || {},
					);
				}
			}
		}
	}


	splitAppsRequest(
		originalAppsRequest: IAppsRequest,
		maxLength: number = 1900,
	) {
		const appsRequest = deepMergeObjects({}, originalAppsRequest);
		const response: IAppsRequest[] = [];
		const { options } = appsRequest;
		if (maxLength > 0) {
			let counter = 0;
			const maxCounter = 1000;
			const { apps = [], manager } = appsRequest;
			let currentAppsRequest: IAppsRequest = {
				manager,
				options,
				apps: [],
			};

			let workingLength = this.getUrlJsonLength(currentAppsRequest);

			for (let appIndex = 0; appIndex < apps.length; appIndex++) {
				if (workingLength) {
					const app = apps[appIndex];
					const appLength = this.getUrlJsonLength(app);
					if (appLength) {
						if (appLength + workingLength < maxLength) {
							if (!currentAppsRequest.apps) {
								currentAppsRequest.apps = [];
							}
							currentAppsRequest.apps.push(app);
							apps.shift();
							appIndex--;
							counter = 0;
						}
						else {
							response.push(currentAppsRequest);
							const { culture, ...otherOptions } = (options ||
								{}) as IAppsRequestOptions;
							currentAppsRequest = {
								options: otherOptions,
								apps: [],
							};
							appIndex--;
							counter++;
							if (counter > maxCounter) {
								break;
							}
						}
					}
					else {
						apps.shift();
						appIndex--;
					}
				}
				workingLength = this.getUrlJsonLength(currentAppsRequest);
			}
			if (currentAppsRequest) {
				response.push(currentAppsRequest);
			}
		}
		else {
			response.push(appsRequest);
		}
		return response;
	}
	getUrlJsonLength(obj: object) {
		try {
			const str = JSON.stringify(obj);
			if (str && str.length > 0) {
				return encodeURIComponent(str).length;
			}
		}
		catch (ex) {
			console.warn('getUrlJsonLength', ex);
		}
		return undefined;
	}
	loadAppsResponseScripts(
		appsResponse: IAppsResponse,
		externalScriptOptions?: TExternalScriptOptions,
		assetRegisterCacheOptions?: CacheOptions,
		appsRenderOptions?: IAppsRender,
	) {
		if (appsResponse.javascript) {
			if (typeof appsResponse.javascript === 'string') {
				const scriptElement = document.createElement('script');
				scriptElement.type = 'text/javascript';
				scriptElement.innerHTML = appsResponse.javascript;
				const currentScript = this.getCurrentScript();
				if (currentScript && currentScript.parentNode) {
					currentScript.parentNode.insertBefore(scriptElement, currentScript);
				}
				appsResponse.javascript = 'true';
			}
		}
		else {
			this.loadAppScripts(appsResponse, externalScriptOptions, assetRegisterCacheOptions, appsRenderOptions);
		}
	}

	loadAppScripts(
		appsResponse: IAppsResponse,
		externalScriptOptions?: TExternalScriptOptions,
		cacheOptions?: CacheOptions,
		appsRenderOptions?: IAppsRender,
	) {
		const { apps } = appsResponse;
		const scriptUrls: IAppUrl[] = (apps || []).map((appResponse) => {
			const { app } = appResponse;
			let fileName = `main-${appResponse.app.templateId}.js`;
			fileName = fileName.replace(/\-v\d{1,5}\.\d{1,5}\.\d{1,5}/, '');
			if (app.assetRegister && app.assetRegister.length > 0) {
				const assetRegister = app.assetRegister.find(asset => asset.fileName === fileName);
				if (assetRegister) {
					fileName = appendCacheQueryString(fileName, assetRegister, cacheOptions);
				}
			}
			return {
				app,
				url: `/applicationManager/apps/${appResponse.app.applicationCode}/${fileName}`,
			};
		});



		const baseUrl = this.externalScriptsBasePath || this.manifest.getBaseUrl('');
		renderApps(scriptUrls, baseUrl, externalScriptOptions, appsRenderOptions);

	}

	async loadAppScriptsForAppsRequest(appsRequest: IAppsRequest, getApplications: () => Promise<IApplication[]>, externalScriptOptions?: TExternalScriptOptions) {
		const applications = (getApplications && await getApplications()) || [];
		const applicationsByApplicationTemplateCode = pivotCollection(applications, application => application.applicationTemplateCode || '_noApplicationTemplateCode');
		const { apps = [] } = appsRequest;
		const scriptUrls = apps.map((app: IApp) => {
			const { templateId, version = 'v1.0.0' } = app;
			const applicationTemplateCode = templateId;
			const applicationTemplateCodeWithVersion = `${applicationTemplateCode}-${version}`;
			const application = applicationsByApplicationTemplateCode[applicationTemplateCodeWithVersion] || applicationsByApplicationTemplateCode[applicationTemplateCode];
			if (application) {
				const { applicationCode } = application;
				let fileName = `main-${app.templateId}.js`;
				fileName = fileName.replace(/\-v\d{1,5}\.\d{1,5}\.\d{1,5}/, '');
				return `/applicationManager/apps/${applicationCode}/${fileName}`;
			}
			console.warn(`No application found for: ${applicationTemplateCode} or ${applicationTemplateCodeWithVersion}`);
			return;
		});

		const baseUrl = this.externalScriptsBasePath || this.manifest.getBaseUrl('');
		for (const scriptUrl of scriptUrls) {
			loadScriptWithOptions(scriptUrl, baseUrl, externalScriptOptions);
		}
	}
	getCurrentScript() {
		return document.currentScript || (function () {
			const scripts = document.getElementsByTagName('script');
			return scripts[scripts.length - 1];
		})();
	}
}

export function getAppRegistry(): IAppRegistry {
	const key = '__ksys-app-registry__';
	if (!(window as any)[key]) {
		(window as any)[key] = {};
	}
	return (window as any)[key];
}

export function getAppsRenderVariables(): IAppsRenderVariables {
	const key = '__ksys-app-render-variables__';
	if (!(window as any)[key]) {
		(window as any)[key] = {};
	}
	return (window as any)[key];
}




export function renderAppsGroup(appsGroup: IAppsRenderGroup, baseUrl: string, externalScriptOptions?: TExternalScriptOptions) {
	if (appsGroup) {
		const appsRenderVariables = getAppsRenderVariables();
		const { appUrlsByAppKey } = appsRenderVariables;
		if (appUrlsByAppKey) {
			const { apps } = appsGroup;
			for (const app of apps) {
				const appKey = getAppKeyRaw(app.templateId, app.configurationKey, app.styleKey, app.version);
				const appsUrl = appUrlsByAppKey[appKey];
				if (appsUrl) {
					loadScriptWithOptions(appsUrl.url, baseUrl, externalScriptOptions, appKey);
				}
			}
		}
	}
}

export function renderApps(appUrls: IAppUrl[], baseUrl: string, externalScriptOptions?: TExternalScriptOptions, appsRenderOptions?: IAppsRender) {
	if (appUrls) {
		for (const appUrl of appUrls) {
			loadScriptWithOptions(appUrl.url, baseUrl, externalScriptOptions);
		}
	}
}

function removeAppsLoadedEventListener() {
	document.removeEventListener(EVENTS.KSYS_APP_LOADED, window['__ksys-app-handle-app-loaded__']);
}

export function loadScriptWithOptions(src: string, baseUrl: string, externalScriptOptions?: TExternalScriptOptions, appKey?: string) {
	if (src) {
		try {
			const externalScript: TExternalScript = {
				src,
				options: externalScriptOptions || {},
			};
			loadScript(externalScript, baseUrl);
			if (appKey) {
				const appsRenderVariables = getAppsRenderVariables();
				const { appUrlsByAppKey } = appsRenderVariables;
				delete appUrlsByAppKey[appKey];
			}
		}
		catch (ex) {
			console.warn(ex);
		}
	}
}